import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { LoadingOverlay } from '@/components/ui/loading';
import { cn } from '@/lib/utils';

export type ModalVariant = 'form' | 'detail' | 'confirmation' | 'fullscreen';
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | 'full';

export interface AppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  variant?: ModalVariant;
  size?: ModalSize;
  isLoading?: boolean;
  isNested?: boolean;
  hideCloseButton?: boolean;
  className?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  full: 'max-w-[95vw]',
};

const variantClasses: Record<ModalVariant, string> = {
  form: 'h-[85vh] sm:h-[90vh] max-h-[600px] sm:max-h-[700px]',
  detail: 'h-[85vh] sm:h-[90vh] max-h-[700px] sm:max-h-[800px]',
  confirmation: 'h-auto max-h-[85vh] sm:max-h-[90vh]',
  fullscreen: 'h-[90vh] sm:h-[95vh] max-h-[90vh] sm:max-h-[95vh]',
};

export const AppModal: React.FC<AppModalProps> = ({
  open,
  onOpenChange,
  children,
  variant = 'form',
  size = 'lg',
  isLoading = false,
  isNested = false,
  hideCloseButton = true,
  className,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          // Base responsive sizing
          'w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-full',
          sizeClasses[size],
          variantClasses[variant],
          // Layout
          'rounded-xl overflow-hidden flex flex-col p-0',
          // Positioning
          'top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]',
          className
        )}
        hideCloseButton={hideCloseButton}
        isNested={isNested}
      >
        <LoadingOverlay isLoading={isLoading}>
          <div className="flex flex-col h-full overflow-hidden w-full">
            {children}
          </div>
        </LoadingOverlay>
      </DialogContent>
    </Dialog>
  );
};
